import React, { useState, useEffect } from 'react';
import ReusableTable from '../dashboard/ReusableTable';
import { getRequest } from '../services/apiService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DepartmentDonutChart from '../dashboard/DepartmentPieChart';

const EquipmentTransaction = () => {
    const [departmentData, setDepartmentData] = useState([]);
    const [departmentLoading, setDepartmentLoading] = useState(true);
    const [detailsData, setDetailsData] = useState({});
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [showDetailsTable, setShowDetailsTable] = useState(false);

    // State for dropdowns
    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedShip, setSelectedShip] = useState(null);
    const [projects, setProjects] = useState([]);
    const [ships, setShips] = useState([]);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            const [deptResponse, projResponse] = await Promise.all([
                getRequest('master/departments/'),
                getRequest('master/projects/'),
            ]);

            if (deptResponse.status === 'success' && projResponse.status === 'success') {
                setDepartments(deptResponse.data);
                setProjects(projResponse.data);

                // Set default department to ID 2
                const defaultDept = deptResponse.data.find(d => d.id === 2);
                setSelectedDepartment(defaultDept);

                // Set default project to ID 1
                const defaultProject = projResponse.data.find(p => p.id === 1);
                setSelectedProject(defaultProject);

                let defaultShip = null;

                // Fetch ships for default project
                if (defaultProject) {
                    const shipsResponse = await getRequest(`master/projects/?parent_id=${defaultProject.id}`);
                    if (shipsResponse.status === 'success') {
                        setShips(shipsResponse.data);
                        defaultShip = shipsResponse.data.find(s => s.id === 12);
                        setSelectedShip(defaultShip);
                    }
                }

                // Fetch initial data
                fetchDepartmentData(defaultDept?.id, null, defaultShip?.id);
            }
        } catch (error) {
            toast.error('Failed to fetch initial data');
            console.error('Error:', error);
        }
    };

    const fetchDepartmentData = async (departmentId, projectId = null, shipId = null) => {
        setDepartmentLoading(true);
        try {
            let url = `transaction/equipment-charts/?equipment__department=${departmentId}`;

            if (shipId) {
                url += `&project_id=${shipId}`;
            } else if (projectId) {
                url += `&project_id=${projectId}`;
            }

            const response = await getRequest(url);
            if (response.summary) {
                const formattedData = Object.entries(response.summary)
                    .filter(([_, value]) => value > 0)
                    .map(([statusKey, value]) => ({
                        status: statusKey,
                        count: value,
                        percentage: ((value / Object.values(response.summary).reduce((a, b) => a + b, 0)) * 100).toFixed(1)
                    }));
                setDepartmentData(formattedData);
                setDetailsData(response.details || {});
            }
        } catch (error) {
            toast.error('Failed to fetch department data');
            console.error('Error fetching department data:', error);
        } finally {
            setDepartmentLoading(false);
        }
    };

    const handleDepartmentChange = (department) => {
        setSelectedDepartment(department);
        setSelectedProject(null);
        setSelectedShip(null);
        fetchDepartmentData(department.id);
    };

    const handleProjectChange = async (project) => {
        setSelectedProject(project);
        setSelectedShip(null);

        try {
            const shipsResponse = await getRequest(`master/projects/?parent_id=${project.id}`);
            if (shipsResponse.status === 'success') {
                setShips(shipsResponse.data);

                // Auto-select ship ID 12 only for project ID 1
                if (project.id === 1) {
                    const defaultShip = shipsResponse.data.find(s => s.id === 12);
                    setSelectedShip(defaultShip);
                    fetchDepartmentData(selectedDepartment.id, null, defaultShip?.id);
                } else {
                    fetchDepartmentData(selectedDepartment.id, project.id);
                }
            }
        } catch (error) {
            toast.error('Failed to fetch ships');
            console.error('Error:', error);
        }
    };

    const handleShipChange = (ship) => {
        setSelectedShip(ship);
        fetchDepartmentData(selectedDepartment.id, null, ship.id);
    };

    const handleClearFilters = () => {
        setSelectedProject(null);
        setSelectedShip(null);
        fetchDepartmentData(selectedDepartment.id);
    };

    const handleStatusClick = (status) => {
        setSelectedStatus(status);
        setShowDetailsTable(true);
    };

    const summaryColumnFields = [
    {
        field: 'status',
        header: 'Status',
        sortable: true,
        body: (rowData) => (
            <button
                onClick={() => handleStatusClick(rowData.status)}
                className="text-black"
            >
                {rowData.status}
            </button>
        )
    },
    { field: 'count', header: 'Count', sortable: true },
    {
        field: 'percentage',
        header: 'Percentage',
        sortable: true,
        body: (rowData) => (
            <div className="flex items-center gap-2">
                <span>{rowData.percentage}%</span>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                        className="h-2.5 rounded-full bg-blue-600"
                        style={{ width: `${rowData.percentage}%` }}
                    />
                </div>
            </div>
        )
    }
];

    const detailsColumnFields = [
        { field: 'task_name', header: 'Task Name', sortable: true },
        { field: 'equipment_name', header: 'Equipment Name', sortable: true },
        { field: 'project_name', header: 'Project Name', sortable: true },
        { field: 'actual_start', header: 'Actual Start', sortable: true },
        { field: 'start_end', header: 'Start End', sortable: true },
        { field: 'actual_end', header: 'Actual End', sortable: true },
        { field: 'actual_completion_percent', header: 'Completion %', sortable: true },
        { field: 'planned_percent', header: 'Planned %', sortable: true }
    ];

    return (
        <div className="p-4">
            <ToastContainer />

            {/* Chart at the top */}
            <div className="mb-8">
                <DepartmentDonutChart
                    selectedDepartment={selectedDepartment}
                    selectedProject={selectedProject}
                    selectedShip={selectedShip}
                    onDepartmentChange={handleDepartmentChange}
                    onProjectChange={handleProjectChange}
                    onShipChange={handleShipChange}
                    onClearFilters={handleClearFilters}
                    departments={departments}
                    projects={projects}
                    ships={ships}
                />
            </div>

            {/* Summary Table */}
            <div className="mb-8">
                <ReusableTable
                    title="Equipment Status Overview"
                    data={departmentData}
                    columnFields={summaryColumnFields}
                    loading={departmentLoading}
                    totalRecords={departmentData.length}
                    rows={10}
                />
            </div>

            {/* Details Table */}
            {showDetailsTable && selectedStatus && detailsData[selectedStatus] && (
                <div className="mb-8">
                    <ReusableTable
                        title={`Detailed View - ${selectedStatus}`}
                        data={detailsData[selectedStatus]}
                        columnFields={detailsColumnFields}
                        loading={departmentLoading}
                        totalRecords={detailsData[selectedStatus].length}
                        rows={10}
                    />
                </div>
            )}
        </div>
    );
};

export default EquipmentTransaction;
